/** Format cents to dollars for product pricing: 5000 → "$50.00", 0 → "Free" */
export function formatPrice(cents: number): string {
  if (cents === 0) return "Free";
  return `$${(cents / 100).toFixed(2)}`;
}

/** Format cents to dollars for revenue/monetary totals: 5000 → "$50.00", 0 → "$0.00" */
export function formatRevenue(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Short per-unit price label for the booking flow. The structured signal is the
 * service's `maxParticipants` (Service.maxParticipants, default 1): a value > 1
 * is a semi-private session that multiple participants share, each paying the
 * per-participant price; 1 is a private lesson with a flat price.
 *   priceUnitLabel(2) → "per participant"   priceUnitLabel(1) → "per lesson"
 */
export function priceUnitLabel(maxParticipants: number): string {
  return maxParticipants > 1 ? "per participant" : "per lesson";
}

/**
 * Total price for a booking, mirroring the booking flow's rule: a semi-private
 * service (maxParticipants > 1) multiplies its per-participant price by the
 * number of participants; a private service is a flat price (count treated as
 * 1). Pure + unit-tested so the booking summary and the charged amount stay in
 * agreement. `baseCents` is the per-participant price (or the summed per-slot
 * total for a recurring private booking).
 */
export function bookingTotalInCents(
  baseCents: number,
  maxParticipants: number,
  participantCount: number,
): number {
  const multiplier = maxParticipants > 1 && participantCount > 0 ? participantCount : 1;
  return baseCents * multiplier;
}

/** Format a date nicely: "Mon, Mar 16, 2026" — deterministic to avoid hydration mismatch */
export function formatDate(date: string | Date): string {
  const d = new Date(date);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Format date with relative labels: "Today", "Tomorrow", "Yesterday", or "Mon, Mar 16, 2026" */
export function formatDateRelative(date: string | Date): string {
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  return formatDate(date);
}

/** Format time: "4:00 PM" — uses manual formatting to avoid hydration mismatch */
export function formatTime(date: string | Date): string {
  const d = new Date(date);
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

/** Format HH:MM to readable: "16:00" → "4:00 PM" */
export function formatTimeString(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}
